import * as React from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/shadcn/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Switch as SSwitch } from '@/components/shadcn/switch'
import { cn } from '@/lib/cn'

export type ListboxOption<T extends string> = {
  value: T
  label: React.ReactNode
  /** Shown before the label, in the button too: a color dot, an avatar, an icon. */
  icon?: React.ReactNode
  /** Muted text after the label. */
  hint?: React.ReactNode
  /** Kept for callers; no longer drawn. */
  sep?: boolean
}

/** The words a filter should match for a label that may be an element. */
function textOf(n: React.ReactNode): string {
  if (n == null || typeof n === 'boolean') return ''
  if (typeof n === 'string' || typeof n === 'number') return String(n)
  if (Array.isArray(n)) return n.map(textOf).join(' ')
  if (React.isValidElement<{ children?: React.ReactNode }>(n)) return textOf(n.props.children)
  return ''
}

/**
 * A dropdown: shadcn's combobox (a button and a Command list in a popover).
 * Arrows move, Enter picks, Escape or a click outside closes. With `search`
 * (on by default past 12 options) a filter box sits at the top of the list.
 */
export function Listbox<T extends string>({ id, value, options, onChange, placeholder = 'Choose…', className, disabled, search, 'aria-label': ariaLabel }: {
  id?: string
  value: T
  options: ListboxOption<T>[]
  onChange: (v: T) => void
  placeholder?: React.ReactNode
  className?: string
  disabled?: boolean
  search?: boolean
  'aria-label'?: string
}) {
  const [open, setOpen] = React.useState(false)
  const cur = options.find((o) => o.value === value)
  const filter = search ?? options.length > 12
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} variant="outline" role="combobox" aria-expanded={open} aria-label={ariaLabel} disabled={disabled}
          className={cn('h-7 min-w-0 justify-between gap-1.5 px-2 text-xs font-normal has-[>svg]:px-2', !cur && 'text-muted-foreground', className)}>
          <span className="flex min-w-0 items-center gap-1.5">
            {cur?.icon}
            <span className="truncate">{cur ? cur.label : placeholder}</span>
          </span>
          <ChevronDown className="size-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-48 p-0">
        <Command>
          {filter && <CommandInput placeholder="Filter…" className="h-8 text-xs" />}
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-xs">No match</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.value} keywords={[textOf(o.label), textOf(o.hint)]}
                  onSelect={() => { onChange(o.value); setOpen(false) }} className="text-xs">
                  {o.icon}
                  <span className="min-w-0 truncate">{o.label}</span>
                  {o.hint && <span className="ml-1 truncate text-muted-foreground">{o.hint}</span>}
                  <Check className={cn('ml-auto size-3.5', o.value === value ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** An on/off switch with an accessible role. */
export function Switch({ id, checked, onChange, disabled, 'aria-labelledby': labelledBy }: { id?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; 'aria-labelledby'?: string }) {
  return <SSwitch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} aria-labelledby={labelledBy} />
}
